/**
 * Auth composable.
 *
 * Provides reactive auth state to any component that needs it.
 * Wraps the auth service so views never import services directly.
 */

import { ref, computed } from 'vue';
import type { UserProfile, OAuthProvider } from '@/types/api';
import * as authService from '@/services/auth';
import { useFlightStorage } from '@/composables/useFlightStorage';
import { isAuthenticated as apiIsAuthenticated } from '@/services/api';
import { syncPostHogUser } from '@/posthog';
import { syncFirebaseAnalyticsUser } from '@/firebaseAnalytics';
import {
  captureFailure,
  clearUser,
  log,
  syncUser,
  warn,
} from '@/lib/appTelemetry';

function syncTelemetryUser(profile: UserProfile | null): void {
  syncUser(profile?.id ?? null);
  syncPostHogUser(profile);
  syncFirebaseAnalyticsUser(profile);
}

// Singleton reactive state (shared across all components)
const user = ref<UserProfile | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const authInitialized = ref(false);

const isLoggedIn = computed(() => user.value !== null);

/**
 * True only when auth has been initialized AND the user has a valid,
 * non-expired session with tokens loaded into the API client.
 */
const isAuthenticated = computed(() => user.value !== null && apiIsAuthenticated());

let authReadyResolve: (() => void) | null = null;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

/** Awaitable gate for the router guard — resolves once init() finishes. */
export function waitForAuthReady(): Promise<void> {
  return authReadyPromise;
}

let initPromise: Promise<void> | null = null;

export function useAuth() {
  /** Call once on app startup to restore any saved session. Idempotent. */
  function init(): Promise<void> {
    if (initPromise) return initPromise;
    initPromise = _doInit();
    return initPromise;
  }

  async function _doInit() {
    isLoading.value = true;
    error.value = null;
    try {
      const session = await authService.restoreSession();
      user.value = session.profile;
      syncTelemetryUser(session.profile);
      log('auth', 'session restore', {
        hasProfile: !!session.profile,
        hasTokens: !!session.tokens?.accessToken,
      });

      // Unblock the router gate immediately using the cached profile so
      // pages render without waiting on a network round-trip. The fresh
      // profile is fetched in the background below.
      authInitialized.value = true;
      if (authReadyResolve) {
        authReadyResolve();
        authReadyResolve = null;
      }

      if (session.tokens?.accessToken) {
        authService.fetchUserProfile().then((refreshed) => {
          if (refreshed.success && refreshed.data) {
            user.value = refreshed.data;
            syncTelemetryUser(refreshed.data);
          }
        });
      }
    } catch (e: any) {
      error.value = e.message ?? 'Failed to restore session';
      warn('auth', 'session restore failed', { message: error.value });
      authInitialized.value = true;
      if (authReadyResolve) {
        authReadyResolve();
        authReadyResolve = null;
      }
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Start OAuth login flow.
   *
   * This will redirect the browser to the OAuth provider.
   * The callback is handled by AuthCallbackPage.vue + handleAuthCallback().
   */
  async function login(provider: OAuthProvider) {
    isLoading.value = true;
    error.value = null;
    try {
      await authService.login(provider);
      // Note: login() redirects the browser, so we won't reach here
    } catch (e: any) {
      error.value = e.message ?? 'Login failed';
      captureFailure('auth', 'oauth login failed', e);
      isLoading.value = false;
    }
  }

  /** Email/password sign-in. On success, sets user; caller should navigate to /record. */
  async function loginWithPassword(email: string, password: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const { profile } = await authService.loginWithPassword(email, password);
      user.value = profile;
      syncTelemetryUser(profile);
      log('auth', 'password login ok');
    } catch (e: any) {
      error.value = e.message ?? 'Sign in failed';
      warn('auth', 'password login failed', { message: error.value });
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  /** Email/password sign-up. On success, sets user; caller should navigate to /record. */
  async function register(params: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) {
    isLoading.value = true;
    error.value = null;
    try {
      const { profile } = await authService.register(params);
      user.value = profile;
      syncTelemetryUser(profile);
      log('auth', 'register ok');
    } catch (e: any) {
      error.value = e.message ?? 'Registration failed';
      warn('auth', 'register failed', { message: error.value });
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  /** Log out and clear local session (and flight data so the next user does not see previous user's flights) */
  async function logout() {
    isLoading.value = true;
    try {
      await authService.logout();
      user.value = null;
      syncTelemetryUser(null);
      clearUser();
      error.value = null;
      log('auth', 'logout');
      // Allow init() to re-run on next login (critical on Android where
      // the app stays alive across the OAuth redirect via deep link).
      initPromise = null;
      const { clearAllFlights } = useFlightStorage();
      await clearAllFlights();
    } finally {
      isLoading.value = false;
    }
  }

  /** Set the user profile directly (used by auth callback) */
  function setUser(profile: UserProfile | null) {
    user.value = profile;
    syncTelemetryUser(profile);
  }

  return {
    user,
    isLoggedIn,
    isAuthenticated,
    authInitialized,
    isLoading,
    error,
    init,
    login,
    loginWithPassword,
    register,
    logout,
    setUser,
  };
}
